import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  Info,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/config/routes";
import CommonLayout from "../../CommonLayout";
import { OrgQueryError } from "../components/OrgQueryError";
import { REQUEST_STATUS_LABELS } from "./constants";
import { Step1BasicInfo } from "@/containers/public/onboarding/steps/Step1BasicInfo";
import { Step2Profile } from "@/containers/public/onboarding/steps/Step2Profile";
import { Step3Collection } from "@/containers/public/onboarding/steps/Step3Collection";
import { Step4Experience } from "@/containers/public/onboarding/steps/Step4Experience";
import { Step5Billing } from "@/containers/public/onboarding/steps/Step5Billing";
import { Step6Compliance } from "@/containers/public/onboarding/steps/Step6Compliance";
import { useMerchantRequest } from "./useMerchantRequest";

const StepTrail = ({
  steps,
  currentStep,
}: {
  steps: { id: number; title: string; subtitle: string }[];
  currentStep: number;
}): JSX.Element => (
  <Flex gap={3} flexDirection={{ base: "column", md: "row" }}>
    {steps.map((step) => {
      const isActive = currentStep === step.id;
      const isPast = currentStep > step.id;

      return (
        <HStack
          key={step.id}
          flex="1"
          gap={3}
          alignItems="center"
          bg="white"
          borderRadius="lg"
          borderWidth="1px"
          borderColor={isActive ? "palomma.300" : "transparent"}
          boxShadow="rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
          p={3}
          opacity={isActive || isPast ? 1 : 0.6}
        >
          <Flex
            w={8}
            h={8}
            flexShrink={0}
            borderRadius="full"
            borderWidth="2px"
            alignItems="center"
            justifyContent="center"
            borderColor={
              isPast ? "palomma.500" : isActive ? "palomma.500" : "gray.300"
            }
            bg={isPast ? "palomma.500" : "transparent"}
            color={isPast ? "white" : isActive ? "palomma.600" : "gray.400"}
          >
            {isPast ? (
              <CheckCircle2 size={16} />
            ) : (
              <Text fontSize="sm" fontWeight="bold">
                {step.id}
              </Text>
            )}
          </Flex>
          <Box minW={0}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.900">
              {step.title}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {step.subtitle}
            </Text>
          </Box>
        </HStack>
      );
    })}
  </Flex>
);

const MerchantRequestContainer = (): JSX.Element => {
  const navigate = useNavigate();
  const o = useMerchantRequest();
  const isLastStep = o.currentStep === o.steps.length;

  const body = (
    <Box spaceY={6} pb={10}>
      <StepTrail steps={o.steps} currentStep={o.currentStep} />

      {o.error && (
        <Box
          borderRadius="xl"
          borderWidth="1px"
          borderColor="red.200"
          bg="red.50"
          p={4}
        >
          <Text fontSize="sm" color="red.600" lineHeight="tall">
            {o.error}
          </Text>
        </Box>
      )}

      {o.isReturned && (
        <Box
          spaceY={4}
          borderRadius="xl"
          borderWidth="1px"
          borderColor="orange.200"
          bg="orange.50"
          p={5}
        >
          <HStack gap={3} alignItems="flex-start">
            <Box flexShrink={0} mt={1}>
              <AlertTriangle
                size={20}
                color="var(--chakra-colors-orange-600)"
              />
            </Box>
            <Box spaceY={1}>
              <Text fontWeight="medium" color="orange.800">
                Más información solicitada
              </Text>
              <Text fontSize="sm" color="orange.700" lineHeight="tall">
                Palomma necesita un poco más de información para crear este
                comercio. Corrige lo que haga falta y vuelve a enviar la
                solicitud desde el último paso.
              </Text>
            </Box>
          </HStack>
          {o.customerFacingSummary && (
            <Box
              borderRadius="lg"
              borderWidth="1px"
              borderColor="orange.100"
              bg="white/50"
              p={4}
            >
              <HStack gap={2} alignItems="flex-start">
                <Box flexShrink={0} mt={1}>
                  <Info size={16} color="var(--chakra-colors-orange-500)" />
                </Box>
                <Text fontSize="sm" color="orange.800" lineHeight="tall">
                  {o.customerFacingSummary}
                </Text>
              </HStack>
            </Box>
          )}
        </Box>
      )}

      {o.currentStep === 1 && (
        <Step1BasicInfo
          data={o.data}
          erps={o.catalogs.erps}
          updateData={o.updateData}
          fieldErrors={o.fieldErrors}
        />
      )}
      {o.currentStep === 2 && (
        <Step2Profile
          data={o.data}
          cities={o.catalogs.cities}
          updateData={o.updateData}
          fieldErrors={o.fieldErrors}
        />
      )}
      {o.currentStep === 3 && (
        <Step3Collection
          data={o.data}
          banks={o.catalogs.banks}
          updateData={o.updateData}
          fieldErrors={o.fieldErrors}
        />
      )}
      {o.currentStep === 4 && (
        <Step4Experience
          data={o.data}
          updateData={o.updateData}
          fieldErrors={o.fieldErrors}
          uploadedDocs={o.uploadedDocs}
          logoPreviewUrl={o.logoPreviewUrl}
          handleFileSelect={o.handleFileSelect}
          handleFileChange={o.handleFileChange}
          fileInputRefs={o.fileInputRefs}
        />
      )}
      {o.currentStep === 5 && (
        <Step5Billing
          data={o.data}
          updateData={o.updateData}
          fieldErrors={o.fieldErrors}
        />
      )}
      {o.currentStep === 6 && (
        <Step6Compliance
          data={o.data}
          updateData={o.updateData}
          fieldErrors={o.fieldErrors}
          uploadedDocs={o.uploadedDocs}
          handleFileSelect={o.handleFileSelect}
          handleFileChange={o.handleFileChange}
          fileInputRefs={o.fileInputRefs}
        />
      )}

      <Flex justifyContent="space-between" alignItems="center" gap={4}>
        {o.currentStep > 1 ? (
          <Button variant="ghost" color="gray.600" onClick={o.prevStep}>
            <ChevronLeft size={18} />
            Atrás
          </Button>
        ) : (
          <Box />
        )}

        <HStack gap={3}>
          {(o.isSaving || o.isUploading) && (
            <Text fontSize="sm" color="gray.400">
              Guardando...
            </Text>
          )}
          {isLastStep ? (
            <Button
              colorPalette="palomma"
              loading={o.isSubmitting}
              onClick={() => void o.handleSubmit()}
            >
              Enviar solicitud
            </Button>
          ) : (
            <Button
              colorPalette="palomma"
              loading={o.isSaving}
              disabled={o.isUploading}
              onClick={() => void o.handleContinue()}
            >
              Continuar
              <ArrowRight size={18} />
            </Button>
          )}
        </HStack>
      </Flex>
    </Box>
  );

  const readOnly = (
    <Box
      borderRadius="xl"
      borderWidth="1px"
      borderColor="gray.200"
      bg="white"
      p={6}
      spaceY={3}
    >
      <Text fontWeight="medium" color="gray.900">
        Esta solicitud está en {REQUEST_STATUS_LABELS[o.status]}
      </Text>
      <Text fontSize="sm" color="gray.600">
        Ya no se puede editar. Te avisaremos cuando Palomma termine la revisión.
      </Text>
      <Button
        variant="outline"
        onClick={() => navigate(ROUTES.DASHBOARD.MERCHANTS)}
      >
        Volver a Comercios
      </Button>
    </Box>
  );

  return (
    <CommonLayout
      title="Agregar comercio"
      subtitle="Los valores de tu organización vienen prellenados; ajusta lo que sea propio de este comercio"
      buttons={
        <Button
          variant="outline"
          onClick={() => navigate(ROUTES.DASHBOARD.MERCHANTS)}
        >
          Cancelar
        </Button>
      }
      table={
        <>
          {o.isLoadError ? (
            <OrgQueryError
              message="la solicitud"
              code={o.loadErrorCode}
              onRetry={o.refetch}
            />
          ) : o.isLoading ? (
            <Text fontSize="sm" color="gray.500">
              Cargando solicitud...
            </Text>
          ) : o.isEditable ? (
            body
          ) : (
            readOnly
          )}
          <style>{`
            [data-onboarding-error="true"] input,
            [data-onboarding-error="true"] textarea {
              border-color: var(--chakra-colors-red-400) !important;
              box-shadow: 0 0 0 1px var(--chakra-colors-red-400) !important;
            }
          `}</style>
        </>
      }
    />
  );
};

export default MerchantRequestContainer;
